import BackendConnect from "./connect";

const POLLING_INVOISE_INTERVAL_MS = 5000;
const POLLING_MAX_ATTEMPTS = 60;

export interface QuestFilters {
  search?: string;
  is_active?: boolean;
  completed?: boolean;
  not_completed?: boolean;
  reward_item_id?: number;
  unblock_item_id?: number;
  limit?: number;
  offset?: number;
  order_by?: string;
  order_dir?: "ASC" | "DESC";
}

export interface QuestData {
  completed_by_user: boolean;
  id: number;
  title: string;
  description: string;
  function_name: string;
  date_start: string;
  date_end: string;
  is_active: boolean;
  rarity_label: string;
  reward_amount: number;
  reward_item_id: number;
  rewards_count: number;
  unblock_item_id: number;
  unblock_amount: number;
  url: string;
  icon?: string;
  [key: string]: any;
}

export interface RewardSessionData {
  id: number;
  quest_id: number;
  user_id: number;
  status: string;
  created_at: string;
  [key: string]: any;
}

export interface SessionRewardItemData {
  id: number;
  date_open: string;
  date_close: string;
  rewarded: boolean;
  is_bonus: boolean;
  unblocked?: boolean;
  reward?: number;
}

class BackendService extends BackendConnect {
  private static instance: BackendService;

  constructor(headers: Record<string, string>) {
    super(headers);
  }

  static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService({});
      const bearerToken = localStorage.getItem("bearerToken");
      if (bearerToken) {
        BackendService.instance.setAccessToken(bearerToken);
      }
    }
    return BackendService.instance;
  }

  // -------------------- QUESTS --------------------

  /** Lists */
  public async getQuestsList(
    completeBy?: string,
    filters?: QuestFilters
  ): Promise<{
    success: boolean;
    quests: QuestData[];
    total: number;
    count: number;
    limit: number | null;
    offset: number;
  }> {
    console.log("quests list called:");
    const questsUrl = `quests/list/${completeBy || ""}`;
    console.log("Quests url:", questsUrl);
    return new Promise(resolve => {
      this.get(questsUrl, filters ? { ...filters } : undefined)
        .then(result =>
          resolve({
            success: result?.success ?? true,
            quests: result?.quests ?? [],
            total: result?.total ?? 0,
            count: result?.count ?? result?.quests?.length ?? 0,
            limit: result?.limit ?? null,
            offset: result?.offset ?? 0,
          })
        )
        .catch(e => {
          console.error("getQuestsList error:", e);
          resolve({
            success: false,
            quests: [],
            total: 0,
            count: 0,
            limit: null,
            offset: 0,
          });
        });
    });
  }

  /** Count */
  public async getQuestsCount(
    completeBy: string,
    filters: QuestFilters = {}
  ): Promise<number> {
    return new Promise(resolve => {
      this.get(`quests/count/${completeBy || ""}`, { ...filters })
        .then(result => resolve(result?.count ?? 0))
        .catch(e => {
          console.error("getQuestsCount error:", e);
          resolve(0);
        });
    });
  }

  /** Check completion */
  public async checkQuestCompletion(data: {
    questid: number;
  }): Promise<{ ok: boolean; isClaimed?: boolean | null }> {
    return new Promise(resolve => {
      this.post("quests/checkcompletion", data)
        .then(result => resolve(result))
        .catch(e => {
          console.error("checkQuestCompletion error:", e);
          resolve({ ok: false });
        });
    });
  }

  /** Complete and reward */
  public async completeAndClaim(data: {
    questid: number;
  }): Promise<{ success?: boolean; amount?: number }> {
    return new Promise(resolve => {
      this.post("quests/completeandclaim", data)
        .then(result => resolve(result))
        .catch(e => {
          console.error("completeAndClaim error:", e);
          resolve({ success: false });
        });
    });
  }

  /** Check if already completed */
  public async isQuestCompleteBy(data: {
    questid: number;
    wallet: string;
  }): Promise<{ completed: boolean }> {
    const { questid, wallet } = data;
    return new Promise(resolve => {
      this.get(`quests/iscompleteby/${questid}/${wallet}`)
        .then(result => resolve(result))
        .catch(e => {
          console.error("isQuestCompleteBy error:", e);
          resolve({ completed: false });
        });
    });
  }

  /** One quest data */
  public async getQuestData(questid: number): Promise<QuestData | null> {
    return new Promise(resolve => {
      this.get(`quests/questdata/${questid}`)
        .then(result => resolve(result ?? null))
        .catch(e => {
          console.error("getQuestData error:", e);
          resolve(null);
        });
    });
  }

  // -------------------- Reward sessions --------------------

  /**
   * List of sessions for multi-reward
   *
   *
   */
  public async getQuestRewardSessions(data: {
    questid: number;
    wallet: string;
  }): Promise<{ sessions: SessionRewardItemData[] }> {
    const { questid, wallet } = data;
    return new Promise(resolve => {
      this.get(`quests/reward/sessions/${questid}/${wallet}`)
        .then(result => resolve({ sessions: result?.sessions ?? [] }))
        .catch(e => {
          console.error("getQuestRewardSessions error:", e);
          resolve({ sessions: [] });
        });
    });
  }

  /** Daily reward in session */
  public async claimRewardInSession(data: {
    questid: number;
    sessionItemId: number;
  }): Promise<{ error?: string; amount?: string; [key: string]: any }> {
    return new Promise(resolve => {
      this.post("quests/claiminsession", data)
        .then(result => resolve(result))
        .catch(e => {
          console.error("claimRewardInSession error:", e);
          resolve({ error: "Network error" });
        });
    });
  }

  /** Init unblock for stars (create invoice in bot) */
  public async initRewardUnblock(data: {
    itemid: number; // itemid - id of session item to unblock
    questid: number;
    sessionItemId?: number;
  }): Promise<{ success?: boolean; error?: string }> {
    return new Promise(resolve => {
      this.post("quests/initunblock", data)
        .then(result => resolve(result))
        .catch(e => {
          console.error("initRewardUnblock error:", e);
          resolve({ success: false, error: "Network error" });
        });
    });
  }

  public async initRewardUnblockInApp(data: {
    itemid: number;
    questid: number;
    sessionItemId: number;
    wallet: string;
    chatId?: number;
  }): Promise<{ success: boolean; sessions?: SessionRewardItemData[] }> {
    const { itemid, questid, wallet, sessionItemId, chatId } = data;

    return new Promise(async (resolve, reject) => {
      try {
        const tg = window.Telegram?.WebApp;
        const isMiniApp =
          typeof window !== "undefined" &&
          tg &&
          !!tg.initData &&
          tg.platform !== "unknown";
        if (isMiniApp && window.Telegram) {
          // mini app logic
          const WebApp = window.Telegram.WebApp;
          const linkRequestBody = {
            itemid,
            questid,
            sessionId: sessionItemId,
            makeUrl: true,
            chatId,
          };
          const { link } = await this.post(
            "quests/initunblock",
            linkRequestBody
          );
          WebApp.openInvoice(link, (status: string) => {
            // Payment processing
            if (status === "failed" || status === "cancelled") {
              // Reject on cancel
              if (interval) clearInterval(interval);
              return reject({ error: `Payment ${status}` });
            }
            if (status === "paid") {
              console.log("Payment detected");
              setTimeout(async () => {
                const { sessions } = await this.getQuestRewardSessions({
                  questid,
                  wallet,
                });
                resolve({ success: true, sessions });
              }, 1001);
            }
          });
        } else {
          const { success } = await this.initRewardUnblock(data);
          resolve({ success: !!success });
        }
        let attempts = 0;
        const interval = setInterval(() => {
          this.getQuestRewardSessions({
            questid,
            wallet,
          })
            .then(res => {
              const { sessions } = res;
              const session = sessions.find(item => item.id === sessionItemId);
              if (session && (session.rewarded || session?.unblocked)) {
                // payment proceed
                clearInterval(interval);
                resolve({ success: true, sessions }); // New session list to update current
              }
            })
            .catch(console.error);
          attempts++;
          if (attempts > POLLING_MAX_ATTEMPTS) {
            // not proceed
            clearInterval(interval);
            reject({ error: "Unblock time to payment expired" });
          }
        }, POLLING_INVOISE_INTERVAL_MS);
      } catch (e) {
        console.error(e);
        reject("Invoise error");
      }
    });
  }
}

export const questsBackend = BackendService.getInstance();
